import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, InjectionToken, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { InstallationIdentityService } from './installation-identity.service';
import { OnboardingDraftService } from './onboarding-draft.service';

export const CAPACITOR_APP = new InjectionToken<typeof App>('CAPACITOR_APP', {
  providedIn: 'root',
  factory: () => App,
});

export const CAPACITOR_UPDATER = new InjectionToken<typeof CapacitorUpdater>('CAPACITOR_UPDATER', {
  providedIn: 'root',
  factory: () => CapacitorUpdater,
});

export const OTA_UPDATES_ENABLED = new InjectionToken<boolean>('OTA_UPDATES_ENABLED', {
  providedIn: 'root',
  factory: () => environment.production,
});

interface OtaConfigResponse {
  enabled: boolean;
  appReviewMode?: boolean;
}

interface OtaCheckResponse {
  version?: string;
  url?: string;
  checksum?: string;
  error?: string;
  resetToBuiltin?: boolean;
}

const OTA_CHECK_THROTTLE_MS = 30 * 60 * 1000;
const OTA_MANUAL_ACTIVATION_BARRIER_VERSION = '999999.0.0';

type PendingOtaActivation =
  | { kind: 'bundle'; bundleId: string }
  | { kind: 'reset' };

@Injectable({
  providedIn: 'root',
})
export class OtaUpdateService {
  private readonly updatesEnabled = inject(OTA_UPDATES_ENABLED);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly installationIdentity = inject(InstallationIdentityService);
  private readonly onboardingDrafts = inject(OnboardingDraftService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly app = inject(CAPACITOR_APP);
  private readonly updater = inject(CAPACITOR_UPDATER);
  private readonly base = environment.apiUrl;

  private initialized = false;
  private nativeListenersAdded = false;
  private checkInFlight: Promise<void> | null = null;
  private lastCheckAt = 0;
  private resolveAngularAppReady!: () => void;
  private readonly angularAppReady = new Promise<void>((resolve) => {
    this.resolveAngularAppReady = resolve;
  });
  private angularAppMarkedReady = false;
  private pendingActivation: PendingOtaActivation | null = null;
  private activationInFlight: Promise<void> | null = null;
  private activationRetryRequested = false;
  private onboardingNavigationInProgress = false;
  private currentBundleId = 'builtin';
  private nativeActivationBarrierReady = false;
  private nativeActivationBarrierInFlight: Promise<void> | null = null;

  constructor() {
    void this.notifyAppReady();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.onboardingNavigationInProgress = this.isOnboardingUrl(event.url);
        if (this.onboardingNavigationInProgress) this.requestPendingActivation();
        return;
      }
      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.onboardingNavigationInProgress = false;
        this.requestPendingActivation();
      }
    });

    effect(() => {
      if (!this.onboardingDrafts.active()) {
        this.requestPendingActivation();
      }
    });

    effect(() => {
      const authInitialized = this.auth.initialized();
      this.auth.currentUser();

      if (!authInitialized || !this.initialized) {
        return;
      }

      void this.syncCustomId();
      void this.checkForUpdate({ force: true });
    });
  }

  initialize(): Promise<void> {
    if (!this.isNativePlatform() || this.initialized) {
      this.initialized = true;
      return Promise.resolve();
    }

    this.initialized = true;
    void this.initializeNative();

    return Promise.resolve();
  }

  markAngularAppReady(): void {
    if (this.angularAppMarkedReady) {
      return;
    }

    this.angularAppMarkedReady = true;
    this.resolveAngularAppReady();
  }

  private async initializeNative(): Promise<void> {
    try {
      await this.ensureNativeActivationBarrier();
    } catch (error) {
      this.reportSilentError('Error setting OTA manual-activation barrier', error);
    }
    await this.syncCustomId();
    try {
      await this.addNativeListeners();
    } catch (error) {
      this.reportSilentError('Error adding OTA app-state listener', error);
    }
    await this.checkForUpdate({ force: true });
  }

  async checkForUpdate(options: { force?: boolean } = {}): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    const now = Date.now();
    if (!options.force && now - this.lastCheckAt < OTA_CHECK_THROTTLE_MS) {
      return;
    }

    if (this.checkInFlight) {
      await this.checkInFlight;
      return;
    }

    this.checkInFlight = this.doCheckForUpdate();
    try {
      await this.checkInFlight;
      this.lastCheckAt = Date.now();
    } finally {
      this.checkInFlight = null;
    }
  }

  private async notifyAppReady(): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    try {
      await this.updater.notifyAppReady();
    } catch (error) {
      this.reportSilentError('Error notifying OTA app ready', error);
    }
  }

  private async addNativeListeners(): Promise<void> {
    if (this.nativeListenersAdded) {
      return;
    }

    await this.app.addListener('appStateChange', (state) => {
      if (state.isActive) {
        void this.checkForUpdate();
      }
    });
    this.nativeListenersAdded = true;
  }

  private async doCheckForUpdate(): Promise<void> {
    try {
      const config = await firstValueFrom(
        this.http.get<OtaConfigResponse>(`${this.base}/api/ota/config`),
      );
      if (!config.enabled || config.appReviewMode) {
        await this.setStatsUrl('');
        return;
      }
      await this.setStatsUrl(environment.otaStatsUrl);

      const payload = await this.buildCheckPayload();
      const response = await firstValueFrom(
        this.http.post<OtaCheckResponse>(`${this.base}/api/ota/check`, payload),
      );

      if (response.resetToBuiltin) {
        await this.angularAppReady;
        this.pendingActivation = { kind: 'reset' };
        await this.activatePendingUpdate();
        return;
      }

      if (!response.version || !response.url || response.error) {
        return;
      }

      const bundle = await this.updater.download({
        version: response.version,
        url: response.url,
        checksum: response.checksum,
      });
      await this.angularAppReady;
      this.pendingActivation = { kind: 'bundle', bundleId: bundle.id };
      await this.activatePendingUpdate();
    } catch (error) {
      this.reportSilentError('Error checking OTA updates', error);
    }
  }

  private async activatePendingUpdate(): Promise<void> {
    if (!this.pendingActivation) return;
    if (this.activationInFlight) {
      this.activationRetryRequested = true;
      await this.activationInFlight;
      return;
    }

    do {
      this.activationRetryRequested = false;
      this.activationInFlight = this.activatePendingUpdateWhenSafe();
      try {
        await this.activationInFlight;
      } finally {
        this.activationInFlight = null;
      }
    } while (this.pendingActivation && this.activationRetryRequested);
  }

  private requestPendingActivation(): void {
    void this.activatePendingUpdate().catch((error) => {
      this.reportSilentError('Error activating pending OTA update', error);
    });
  }

  private async activatePendingUpdateWhenSafe(): Promise<void> {
    const pending = this.pendingActivation;
    if (!pending || await this.isActivationBlocked()) return;
    if (pending !== this.pendingActivation || this.hasImmediateOnboardingBlocker()) return;

    if (pending.kind === 'reset') {
      await this.updater.reset({ toLastSuccessful: false });
    } else {
      await this.ensureNativeActivationBarrier();
      if (pending !== this.pendingActivation || await this.isActivationBlocked()) return;
      // Queue first: Capgo's direct set() reload can strand foreground Android WebViews.
      await this.updater.next({ id: pending.bundleId });
      if (pending !== this.pendingActivation || await this.isActivationBlocked()) {
        await this.updater.next({ id: this.currentBundleId });
        return;
      }
      await this.updater.reload();
    }
    this.pendingActivation = null;
  }

  private async ensureNativeActivationBarrier(): Promise<void> {
    if (this.nativeActivationBarrierReady) return;
    if (this.nativeActivationBarrierInFlight) {
      await this.nativeActivationBarrierInFlight;
      return;
    }

    // El plugin instala `next` al pasar a segundo plano incluso en modo manual.
    // Este cerrojo nativo persiste entre reinicios; reload() lo omite y sigue bajo control de este servicio.
    this.nativeActivationBarrierInFlight = this.updater.setMultiDelay({
      delayConditions: [{
        kind: 'nativeVersion',
        value: OTA_MANUAL_ACTIVATION_BARRIER_VERSION,
      }],
    });
    try {
      await this.nativeActivationBarrierInFlight;
      this.nativeActivationBarrierReady = true;
    } finally {
      this.nativeActivationBarrierInFlight = null;
    }
  }

  private async isActivationBlocked(): Promise<boolean> {
    if (this.hasImmediateOnboardingBlocker()) return true;
    const hasActiveDraft = await this.onboardingDrafts.hasActiveDraft();
    return hasActiveDraft || this.hasImmediateOnboardingBlocker();
  }

  private hasImmediateOnboardingBlocker(): boolean {
    return this.onboardingNavigationInProgress
      || this.onboardingDrafts.active()
      || this.isOnboardingUrl(this.router.url);
  }

  private isOnboardingUrl(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return path === '/onboarding' || path.startsWith('/onboarding/');
  }

  private async buildCheckPayload(): Promise<Record<string, string | number>> {
    const current = await this.updater.current();
    this.currentBundleId = current.bundle.id || 'builtin';
    const appInfo = await this.app.getInfo();
    const installationId = await this.installationIdentity.getId();

    return {
      platform: Capacitor.getPlatform(),
      device_id: installationId,
      app_id: appInfo.id,
      custom_id: this.auth.currentUser()?.pk ?? '',
      version_name: current.bundle.version || current.bundle.id || 'builtin',
      version_build: appInfo.version,
      version_code: appInfo.build,
      plugin_version: '',
    };
  }

  private async syncCustomId(): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    try {
      const installationId = await this.installationIdentity.getId();
      await this.updater.setCustomId({
        customId: installationId,
      });
    } catch (error) {
      this.reportSilentError('Error setting OTA custom id', error);
    }
  }

  private async setStatsUrl(url: string): Promise<void> {
    try {
      await this.updater.setStatsUrl({ url });
    } catch (error) {
      this.reportSilentError('Error setting OTA stats URL', error);
    }
  }

  private isNativePlatform(): boolean {
    return this.updatesEnabled && Capacitor.isNativePlatform();
  }

  private reportSilentError(message: string, error: unknown): void {
    console.warn(message, error);
  }
}
